import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PromoCodesAdmin from './PromoCodesAdmin';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Users, DollarSign, FileText, TrendingUp, Search, Ban, CheckCircle, Loader2, Star, Trash2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminDashboard = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [revenue, setRevenue] = useState(null);
  
  const [userFilter, setUserFilter] = useState('all');
  const [quoteFilter, setQuoteFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated || user?.userType !== 'admin') {
      navigate('/');
      return;
    }
    
    fetchAllData();
  }, [authLoading, isAuthenticated, user, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, quotesRes, providersRes, revenueRes, analyticsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/users`),
        axios.get(`${API_URL}/api/admin/quotes`),
        axios.get(`${API_URL}/api/admin/providers`),
        axios.get(`${API_URL}/api/admin/revenue`),
        axios.get(`${API_URL}/api/admin/analytics`)
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setQuotes(quotesRes.data);
      setProviders(providersRes.data);
      setRevenue(revenueRes.data);
      setAnalyticsData(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load admin data.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUserStatusChange = async (userId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/admin/users/${userId}/status?status=${newStatus}`);
      setUsers(users.map(u => u.id === userId ? { ...u, status: newStatus } : u));
      toast({
        title: 'Status Updated',
        description: `User status changed to ${newStatus}.`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update user status.',
        variant: 'destructive'
      });
    }
  };

  const handleProviderFeatured = async (providerId, featured) => {
    try {
      await axios.patch(`${API_URL}/api/admin/providers/${providerId}/featured?featured=${featured}`);
      setProviders(providers.map(p => p.id === providerId ? { ...p, featured } : p));
      toast({
        title: 'Featured Status Updated',
        description: `Provider ${featured ? 'featured' : 'unfeatured'} successfully.`
      });
    } catch (error) {
      console.error('Error updating featured status:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update provider.',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteProvider = async () => {
    if (!providerToDelete) return;
    
    const { providerId, providerName } = providerToDelete;
    console.log('Deleting provider:', providerId, providerName);
    
    try {
      console.log('Sending delete request to:', `${API_URL}/api/admin/providers/${providerId}`);
      const response = await axios.delete(`${API_URL}/api/admin/providers/${providerId}`);
      console.log('Delete response:', response.data);
      
      setProviders(providers.filter(p => p.id !== providerId));
      setDeleteModalOpen(false);
      setProviderToDelete(null);
      
      toast({
        title: 'Provider Deleted',
        description: `${providerName} has been permanently removed.`
      });
    } catch (error) {
      console.error('Error deleting provider:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete provider.',
        variant: 'destructive'
      });
    }
  };

  const openDeleteModal = (providerId, providerName) => {
    console.log('Opening delete modal for provider:', providerId, providerName);
    setProviderToDelete({ providerId, providerName });
    setDeleteModalOpen(true);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated || user?.userType !== 'admin') {
    return null;
  }

  const filteredUsers = users.filter(u => {
    if (userFilter !== 'all' && u.userType !== userFilter) return false;
    if (searchTerm && !u.name.toLowerCase().includes(searchTerm.toLowerCase()) && !u.email.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const filteredQuotes = quotes.filter(q => {
    if (quoteFilter !== 'all' && q.status !== quoteFilter) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage users, quotes, providers, and revenue</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          </div>
        ) : (
          <>
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Users</p>
                      <p className="text-3xl font-bold text-gray-900">{stats?.users.total}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {stats?.users.professionals} pros / {stats?.users.homeowners} homeowners
                      </p>
                    </div>
                    <Users className="h-10 w-10 text-blue-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Quotes</p>
                      <p className="text-3xl font-bold text-gray-900">{stats?.quotes.total}</p>
                      <p className="text-xs text-gray-500 mt-1">{stats?.quotes.pending} pending</p>
                    </div>
                    <FileText className="h-10 w-10 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                      <p className="text-3xl font-bold text-gray-900">${stats?.revenue.total.toFixed(2)}</p>
                      <p className="text-xs text-gray-500 mt-1">{stats?.revenue.paid} paid transactions</p>
                    </div>
                    <DollarSign className="h-10 w-10 text-orange-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Subscriptions</p>
                      <p className="text-3xl font-bold text-gray-900">{stats?.users.bySubscription.professional + stats?.users.bySubscription.premium}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {stats?.users.bySubscription.professional} pro / {stats?.users.bySubscription.premium} premium
                      </p>
                    </div>
                    <TrendingUp className="h-10 w-10 text-purple-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="users" className="space-y-6">
              <TabsList className="grid w-full grid-cols-6">
                <TabsTrigger value="users">Users</TabsTrigger>
                <TabsTrigger value="quotes">Quotes</TabsTrigger>
                <TabsTrigger value="providers">Providers</TabsTrigger>
                <TabsTrigger value="revenue">Revenue</TabsTrigger>
                <TabsTrigger value="promo">Promo Codes</TabsTrigger>
                <TabsTrigger value="analytics">Analytics</TabsTrigger>
              </TabsList>

              {/* Users Tab */}
              <TabsContent value="users">
                <Card>
                  <CardHeader>
                    <CardTitle>User Management</CardTitle>
                    <div className="flex gap-4 mt-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search users..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                      <select
                        value={userFilter}
                        onChange={(e) => setUserFilter(e.target.value)}
                        className="px-3 py-2 border rounded-md"
                      >
                        <option value="all">All Users</option>
                        <option value="homeowner">Homeowners</option>
                        <option value="professional">Professionals</option>
                      </select>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="border-b">
                          <tr className="text-left">
                            <th className="pb-3 font-semibold">Name</th>
                            <th className="pb-3 font-semibold">Email</th>
                            <th className="pb-3 font-semibold">Type</th>
                            <th className="pb-3 font-semibold">Subscription</th>
                            <th className="pb-3 font-semibold">Status</th>
                            <th className="pb-3 font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers.map(user => (
                            <tr key={user.id} className="border-b">
                              <td className="py-3">{user.name}</td>
                              <td className="py-3 text-sm text-gray-600">{user.email}</td>
                              <td className="py-3">
                                <Badge variant={user.userType === 'professional' ? 'default' : 'secondary'}>
                                  {user.userType}
                                </Badge>
                              </td>
                              <td className="py-3">{user.subscriptionTier || 'free'}</td>
                              <td className="py-3">
                                <Badge variant={user.status === 'active' ? 'default' : 'destructive'}>
                                  {user.status || 'active'}
                                </Badge>
                              </td>
                              <td className="py-3">
                                {(user.status || 'active') === 'active' ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUserStatusChange(user.id, 'suspended')}
                                    className="text-red-600"
                                  >
                                    <Ban className="h-4 w-4 mr-1" />
                                    Suspend
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUserStatusChange(user.id, 'active')}
                                    className="text-green-600"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Activate
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Quotes Tab */}
              <TabsContent value="quotes">
                <Card>
                  <CardHeader>
                    <CardTitle>Quote Management</CardTitle>
                    <div className="flex gap-4 mt-4">
                      <select
                        value={quoteFilter}
                        onChange={(e) => setQuoteFilter(e.target.value)}
                        className="px-3 py-2 border rounded-md"
                      >
                        <option value="all">All Quotes</option>
                        <option value="pending">Pending</option>
                        <option value="accepted">Accepted</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="border-b">
                          <tr className="text-left">
                            <th className="pb-3 font-semibold">Service</th>
                            <th className="pb-3 font-semibold">Customer</th>
                            <th className="pb-3 font-semibold">Email</th>
                            <th className="pb-3 font-semibold">Status</th>
                            <th className="pb-3 font-semibold">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredQuotes.map(quote => (
                            <tr key={quote.id} className="border-b">
                              <td className="py-3 font-medium">{quote.service}</td>
                              <td className="py-3">{quote.name}</td>
                              <td className="py-3 text-sm text-gray-600">{quote.email}</td>
                              <td className="py-3">
                                <Badge 
                                  variant={quote.status === 'accepted' ? 'default' : quote.status === 'pending' ? 'secondary' : 'destructive'}
                                >
                                  {quote.status}
                                </Badge>
                              </td>
                              <td className="py-3 text-sm">{new Date(quote.createdAt).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Providers Tab */}
              <TabsContent value="providers">
                <Card>
                  <CardHeader>
                    <CardTitle>Provider Management</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {providers.map(provider => (
                        <Card key={provider.id}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <img src={provider.image} alt={provider.name} className="w-16 h-16 rounded-lg object-cover" />
                                <div>
                                  <h3 className="font-semibold">{provider.name}</h3>
                                  <p className="text-sm text-gray-600">{provider.location}</p>
                                  <p className="text-sm text-gray-500">Rating: {provider.rating}</p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant={provider.featured ? 'default' : 'outline'}
                                  onClick={() => handleProviderFeatured(provider.id, !provider.featured)}
                                >
                                  <Star className={`h-4 w-4 mr-1 ${provider.featured ? 'fill-current' : ''}`} />
                                  {provider.featured ? 'Featured' : 'Feature'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => openDeleteModal(provider.id, provider.name)}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Remove
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Revenue Tab */}
              <TabsContent value="revenue">
                <Card>
                  <CardHeader>
                    <CardTitle>Revenue Analytics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">Total Revenue</h3>
                        <p className="text-3xl font-bold text-green-600">${revenue?.totalRevenue.toFixed(2)}</p>
                      </div>
                      <p className="text-sm text-gray-600">From {revenue?.transactions.length} transactions</p>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="border-b">
                          <tr className="text-left">
                            <th className="pb-3 font-semibold">Date</th>
                            <th className="pb-3 font-semibold">Amount</th>
                            <th className="pb-3 font-semibold">Email</th>
                            <th className="pb-3 font-semibold">Package</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenue?.transactions.map((transaction, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="py-3 text-sm">{new Date(transaction.createdAt).toLocaleDateString()}</td>
                              <td className="py-3 font-semibold text-green-600">${transaction.amount.toFixed(2)}</td>
                              <td className="py-3 text-sm">{transaction.email || 'N/A'}</td>
                              <td className="py-3 text-sm">{transaction.metadata?.tier || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Promo Codes Tab */}
              <TabsContent value="promo">
                <PromoCodesAdmin />
              </TabsContent>


              {/* Analytics Tab */}
              <TabsContent value="analytics">
                {analyticsData ? (
                  <div className="space-y-6">
                    {/* Usage Analytics */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-orange-600" />
                          Usage Analytics
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <h3 className="font-semibold text-blue-900">Page Views</h3>
                            <p className="text-2xl font-bold text-blue-700">{analyticsData.usage_analytics.page_views.toLocaleString()}</p>
                            <p className="text-sm text-blue-600">Estimated from activity</p>
                          </div>
                          <div className="bg-green-50 p-4 rounded-lg">
                            <h3 className="font-semibold text-green-900">Active Users</h3>
                            <p className="text-2xl font-bold text-green-700">{analyticsData.usage_analytics.active_users}</p>
                            <p className="text-sm text-green-600">Last 30 days</p>
                          </div>
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <h3 className="font-semibold text-purple-900">Total Users</h3>
                            <p className="text-2xl font-bold text-purple-700">{analyticsData.usage_analytics.total_users}</p>
                            <p className="text-sm text-purple-600">All time registered</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Search & Service Analytics */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Search className="h-5 w-5 text-orange-600" />
                          Search & Service Analytics
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div>
                            <h3 className="font-semibold mb-4">Top Requested Services</h3>
                            <div className="space-y-3">
                              {analyticsData.service_analytics.top_services.map((service, index) => (
                                <div key={index} className="flex justify-between items-center">
                                  <span>{service.name}</span>
                                  <div className="flex items-center gap-2">
                                    <div className="w-20 bg-gray-200 rounded-full h-2">
                                      <div className="bg-orange-600 h-2 rounded-full" style={{width: `${Math.min(service.percentage, 100)}%`}}></div>
                                    </div>
                                    <span className="text-sm text-gray-600">{service.count}</span>
                                  </div>
                                </div>
                              ))}
                              {analyticsData.service_analytics.top_services.length === 0 && (
                                <p className="text-gray-500 text-center py-4">No service data available yet</p>
                              )}
                            </div>
                          </div>
                          
                          <div>
                            <h3 className="font-semibold mb-4">Geographic Demand</h3>
                            <div className="space-y-3">
                              {analyticsData.service_analytics.geographic_demand.map((location, index) => (
                                <div key={index} className="flex justify-between items-center">
                                  <span>{location.location}</span>
                                  <div className="flex items-center gap-2">
                                    <div className="w-20 bg-gray-200 rounded-full h-2">
                                      <div className="bg-blue-600 h-2 rounded-full" style={{width: `${Math.min(location.percentage, 100)}%`}}></div>
                                    </div>
                                    <span className="text-sm text-gray-600">{location.count}</span>
                                  </div>
                                </div>
                              ))}
                              {analyticsData.service_analytics.geographic_demand.length === 0 && (
                                <p className="text-gray-500 text-center py-4">No location data available yet</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Performance Metrics */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-orange-600" />
                          Performance & Conversion Metrics
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div className="bg-yellow-50 p-4 rounded-lg">
                            <h3 className="font-semibold text-yellow-900">Lead Response Rate</h3>
                            <p className="text-2xl font-bold text-yellow-700">{analyticsData.performance_metrics.lead_response_rate.toFixed(1)}%</p>
                            <p className="text-sm text-yellow-600">Avg response: {analyticsData.performance_metrics.avg_response_time_hours}hrs</p>
                          </div>
                          <div className="bg-indigo-50 p-4 rounded-lg">
                            <h3 className="font-semibold text-indigo-900">Quote Acceptance</h3>
                            <p className="text-2xl font-bold text-indigo-700">{analyticsData.performance_metrics.quote_acceptance_rate}%</p>
                            <p className="text-sm text-indigo-600">Quotes accepted by customers</p>
                          </div>
                          <div className="bg-pink-50 p-4 rounded-lg">
                            <h3 className="font-semibold text-pink-900">Provider Rating</h3>
                            <p className="text-2xl font-bold text-pink-700">{analyticsData.performance_metrics.avg_provider_rating}/5</p>
                            <p className="text-sm text-pink-600">Average provider rating</p>
                          </div>
                          <div className="bg-teal-50 p-4 rounded-lg">
                            <h3 className="font-semibold text-teal-900">Platform Growth</h3>
                            <p className="text-2xl font-bold text-teal-700">{analyticsData.performance_metrics.platform_growth > 0 ? '+' : ''}{analyticsData.performance_metrics.platform_growth}%</p>
                            <p className="text-sm text-teal-600">Month-over-month</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Technical Analytics */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-orange-600" />
                          Technical Analytics
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div>
                            <h3 className="font-semibold mb-4">Device Usage</h3>
                            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span>Mobile</span>
                                <div className="flex items-center gap-2">
                                  <div className="w-24 bg-gray-200 rounded-full h-2">
                                    <div className="bg-green-600 h-2 rounded-full" style={{width: `${analyticsData.technical_analytics.device_usage.mobile}%`}}></div>
                                  </div>
                                  <span className="text-sm text-gray-600">{analyticsData.technical_analytics.device_usage.mobile}%</span>
                                </div>
                              </div>
                              <div className="flex justify-between items-center">
                                <span>Desktop</span>
                                <div className="flex items-center gap-2">
                                  <div className="w-24 bg-gray-200 rounded-full h-2">
                                    <div className="bg-blue-600 h-2 rounded-full" style={{width: `${analyticsData.technical_analytics.device_usage.desktop}%`}}></div>
                                  </div>
                                  <span className="text-sm text-gray-600">{analyticsData.technical_analytics.device_usage.desktop}%</span>
                                </div>
                              </div>
                              <div className="flex justify-between items-center">
                                <span>Tablet</span>
                                <div className="flex items-center gap-2">
                                  <div className="w-24 bg-gray-200 rounded-full h-2">
                                    <div className="bg-purple-600 h-2 rounded-full" style={{width: `${analyticsData.technical_analytics.device_usage.tablet}%`}}></div>
                                  </div>
                                  <span className="text-sm text-gray-600">{analyticsData.technical_analytics.device_usage.tablet}%</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <h3 className="font-semibold mb-4">Platform Summary</h3>
                            <div className="space-y-3">
                              <div className="bg-gray-50 p-3 rounded">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm">Total Providers</span>
                                  <span className="font-semibold text-green-600">{analyticsData.summary.total_providers}</span>
                                </div>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm">Total Quotes</span>
                                  <span className="font-semibold text-blue-600">{analyticsData.summary.total_quotes}</span>
                                </div>
                              </div>
                              <div className="bg-gray-50 p-3 rounded">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm">Featured Providers</span>
                                  <span className="font-semibold text-orange-600">{analyticsData.summary.featured_providers}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Data Privacy Notice */}
                    <Card className="border-orange-200 bg-orange-50">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center">
                              <FileText className="h-4 w-4 text-white" />
                            </div>
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-2">Data Privacy & Usage</h3>
                            <p className="text-sm text-gray-700 mb-3">
                              All data shown above is calculated from your platform's database. Individual user information is never shared or displayed.
                            </p>
                            <div className="text-xs text-gray-600 space-y-1">
                              <p>• Personal information (names, emails, addresses) is excluded</p>
                              <p>• Location data is aggregated to city level only</p>
                              <p>• Individual behavior patterns are not tracked</p>
                              <p>• Users can opt out via Data Sharing setting</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading analytics data...</p>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      <Footer />
      <Toaster />

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Provider</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to delete <span className="font-bold">{providerToDelete?.providerName}</span>?
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800 font-medium">⚠️ This action will permanently:</p>
              <ul className="text-red-700 text-sm mt-2 space-y-1 ml-4">
                <li>• Delete the provider profile</li>
                <li>• Remove all associated quotes</li>
                <li>• Delete all reviews</li>
                <li>• Remove all packages</li>
              </ul>
              <p className="text-red-800 font-bold text-sm mt-3">This cannot be undone!</p>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setProviderToDelete(null);
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteProvider}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Permanently
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
